import type { MockDatabase, MockRecord, SavedTable } from '../database.ts';
import { recordKey, splitKey, type SyncChange, type SyncMessage } from './protocol.ts';
import { isSyncedRecord, isSyncedTable, mayOriginate, mayReceive, type SyncPortal } from './rules.ts';

export const SYNC_STATE_KEY = 'qcdoc.prototype.sync.v1';
const BATCH_SIZE = 100;

/** [clock of the latest local version, 1 when it is a deletion, latest clock the peer acknowledged]. */
type Entry = [clock: string, deleted: 0 | 1, acked: string];
interface SyncState {
  v: 1;
  /** Clock of the most recent reset; a newer epoch always wins over older data. */
  epoch: string;
  last: string;
  records: Record<string, Entry>;
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ReplicaPort {
  database(): MockDatabase;
  /** Persists the in-memory database. */
  persist(): void;
  /** Runs the task when no mock transaction is in flight. */
  exclusive<T>(task: () => T): Promise<T>;
  /** Restores the seeded database. */
  wipe(): void;
}

export type ReplicaEvent =
  | { type: 'applied'; tables: string[] }
  | { type: 'reset'; remote: boolean }
  | { type: 'state' };

export interface ReplicaOptions {
  /** The portal this replica runs in; decides which shared tables it may author or accept. */
  portal: SyncPortal;
  port: ReplicaPort;
  store: KeyValueStore;
  now?: () => number;
  instance?: string;
}

const isPlainRecord = (value: unknown): value is MockRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

function parseClock(clock: string): [wall: number, counter: number] {
  return clock ? [parseInt(clock.slice(0, 9), 36), parseInt(clock.slice(10, 13), 36)] : [0, 0];
}

function emptyState(): SyncState {
  return { v: 1, epoch: '', last: '', records: {} };
}

function mergeStates(left: SyncState, right: SyncState): SyncState {
  if (left.epoch !== right.epoch) return left.epoch > right.epoch ? left : right;
  const records = { ...right.records };
  for (const [key, entry] of Object.entries(left.records)) {
    const other = records[key];
    if (!other || entry[0] > other[0]) records[key] = entry;
    else if (entry[0] === other[0] && entry[2] > other[2]) records[key] = [other[0], other[1], entry[2]];
  }
  return { v: 1, epoch: left.epoch, last: left.last > right.last ? left.last : right.last, records };
}

/**
 * Record-level, last-writer-wins replica of the mock database. It only tracks which records changed,
 * so pending changes survive reloads and are exchanged whenever a peer becomes reachable.
 */
export class SyncReplica {
  private state: SyncState;
  private applying = false;
  private readonly listeners = new Set<(event: ReplicaEvent) => void>();
  private readonly port: ReplicaPort;
  private readonly store: KeyValueStore;
  private readonly now: () => number;
  private readonly instance: string;
  private readonly portal: SyncPortal;

  constructor(options: ReplicaOptions) {
    this.portal = options.portal;
    this.port = options.port;
    this.store = options.store;
    this.now = options.now ?? Date.now;
    this.instance = options.instance ?? Math.random().toString(36).slice(2, 10).padEnd(8, '0');
    this.state = this.read() ?? emptyState();
  }

  get epoch(): string {
    return this.state.epoch;
  }

  subscribe(listener: (event: ReplicaEvent) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  pendingCount(): number {
    return Object.values(this.state.records).filter((entry) => entry[2] < entry[0]).length;
  }

  hello(reply = true): SyncMessage {
    return { type: 'hello', epoch: this.state.epoch, digest: this.digest(), reply };
  }

  /** Records the record-level difference of a database save and returns it for immediate delivery. */
  onSaved(changed: SavedTable[]): SyncChange[] {
    if (this.applying) return [];
    const items: SyncChange[] = [];
    for (const { table, previous, records } of changed) {
      if (!isSyncedTable(table) || !mayOriginate(table, this.portal)) continue;
      const before = new Map<string, string>();
      for (const record of previous ? parseRecords(previous) : []) if (isSyncedRecord(table, record)) before.set(record.oid, JSON.stringify(record));
      const after = new Map<string, { json: string; record: MockRecord }>();
      for (const record of records) {
        if (isPlainRecord(record) && typeof record.oid === 'string' && record.oid && isSyncedRecord(table, record)) after.set(record.oid, { json: JSON.stringify(record), record });
      }
      for (const [oid, { json, record }] of after) {
        if (before.get(oid) !== json) items.push({ key: recordKey(table, oid), c: this.tick(), r: record });
      }
      for (const oid of before.keys()) {
        if (!after.has(oid)) items.push({ key: recordKey(table, oid), c: this.tick(), d: 1 });
      }
    }
    if (!items.length) return items;
    for (const item of items) this.state.records[item.key] = [item.c, item.d ? 1 : 0, ''];
    this.persistState();
    this.emit({ type: 'state' });
    return items;
  }

  /** Starts a new epoch after the user resets the local data; returns the message peers must receive. */
  onLocalReset(): SyncMessage | undefined {
    if (this.applying) return undefined;
    const epoch = this.tick();
    this.state = { ...this.state, epoch, records: {} };
    this.persistState();
    this.emit({ type: 'reset', remote: false });
    return { type: 'reset', epoch };
  }

  /** Picks up state persisted by another tab of the same portal. */
  refresh(): void {
    const stored = this.read();
    if (!stored) return;
    const previousEpoch = this.state.epoch;
    this.state = mergeStates(this.state, stored);
    this.emit(this.state.epoch !== previousEpoch ? { type: 'reset', remote: true } : { type: 'state' });
  }

  /** Messages carrying every change the peer has not acknowledged. */
  pendingChanges(): Promise<SyncMessage[]> {
    return this.port.exclusive(() => this.changeMessages(Object.keys(this.state.records)
      .filter((key) => this.state.records[key][2] < this.state.records[key][0])));
  }

  async receive(message: SyncMessage): Promise<SyncMessage[]> {
    if (message.type === 'reset') return this.handleReset(message.epoch);
    if (message.epoch < this.state.epoch) return message.type === 'ack' ? [] : [{ type: 'reset', epoch: this.state.epoch }];
    if (message.epoch > this.state.epoch) await this.adoptReset(message.epoch);
    switch (message.type) {
      case 'hello': return this.handleHello(message.digest, message.reply);
      case 'changes': return this.handleChanges(message.items);
      case 'ack': this.handleAck(message.acks); return [];
    }
  }

  private async handleReset(epoch: string): Promise<SyncMessage[]> {
    if (epoch < this.state.epoch) return [{ type: 'reset', epoch: this.state.epoch }];
    if (epoch === this.state.epoch) return [];
    await this.adoptReset(epoch);
    return [this.hello(true)];
  }

  private adoptReset(epoch: string): Promise<void> {
    return this.port.exclusive(() => {
      this.applying = true;
      try { this.port.wipe(); }
      finally { this.applying = false; }
      this.state = { v: 1, epoch, last: this.state.last > epoch ? this.state.last : epoch, records: {} };
      this.persistState();
    }).then(() => { this.emit({ type: 'reset', remote: true }); });
  }

  private handleHello(digest: Record<string, string>, reply: boolean): Promise<SyncMessage[]> {
    return this.port.exclusive(() => {
      const replies: SyncMessage[] = reply ? [this.hello(false)] : [];
      const missing: string[] = [];
      let acknowledged = false;
      for (const [key, entry] of Object.entries(this.state.records)) {
        const peerClock = digest[key];
        if (peerClock !== undefined && peerClock >= entry[0]) {
          if (entry[2] !== entry[0]) { entry[2] = entry[0]; acknowledged = true; }
        } else missing.push(key);
      }
      if (acknowledged) { this.persistState(); this.emit({ type: 'state' }); }
      return [...replies, ...this.changeMessages(missing)];
    });
  }

  private handleChanges(items: SyncChange[]): Promise<SyncMessage[]> {
    return this.port.exclusive(() => {
      const acks: Record<string, string> = {};
      const newer: string[] = [];
      const edits = new Map<string, Map<string, MockRecord | null>>();
      for (const item of items) {
        const parts = splitKey(item.key);
        if (!parts || !isSyncedTable(parts.table) || !mayReceive(parts.table, this.portal) || (item.r && (item.r.oid !== parts.oid || !isSyncedRecord(parts.table, item.r)))) { acks[item.key] = item.c; continue; }
        const existing = this.state.records[item.key];
        if (existing && existing[0] >= item.c) {
          acks[item.key] = existing[0];
          if (existing[0] > item.c) newer.push(item.key);
          continue;
        }
        let table = edits.get(parts.table);
        if (!table) edits.set(parts.table, table = new Map());
        table.set(parts.oid, item.d ? null : structuredClone(item.r!));
        this.state.records[item.key] = [item.c, item.d ? 1 : 0, item.c];
        if (item.c > this.state.last) this.state.last = item.c;
        acks[item.key] = item.c;
      }
      const tables = this.applyEdits(edits);
      this.persistState();
      this.emit(tables.length ? { type: 'applied', tables } : { type: 'state' });
      return [{ type: 'ack', epoch: this.state.epoch, acks }, ...this.changeMessages(newer)];
    });
  }

  private handleAck(acks: Record<string, string>): void {
    let acknowledged = false;
    for (const [key, clock] of Object.entries(acks)) {
      const entry = this.state.records[key];
      if (entry && clock >= entry[0] && entry[2] !== entry[0]) { entry[2] = entry[0]; acknowledged = true; }
    }
    if (!acknowledged) return;
    this.persistState();
    this.emit({ type: 'state' });
  }

  private applyEdits(edits: Map<string, Map<string, MockRecord | null>>): string[] {
    if (!edits.size) return [];
    const database = this.port.database();
    const touched: string[] = [];
    for (const [table, replace] of edits) {
      let rows = Object.hasOwn(database, table) ? database[table] : undefined;
      if (rows !== undefined && !Array.isArray(rows)) continue;
      if (!rows) database[table] = rows = [];
      const next: MockRecord[] = [];
      const placed = new Set<string>();
      let changed = false;
      for (const row of rows) {
        const synced = isSyncedRecord(table, row);
        if (!synced) placed.add(row.oid);
        const replacement = synced ? replace.get(row.oid) : undefined;
        if (replacement === undefined) next.push(row);
        else if (replacement === null || placed.has(row.oid)) changed = true;
        else {
          placed.add(row.oid);
          if (JSON.stringify(row) === JSON.stringify(replacement)) next.push(row);
          else { next.push(replacement); changed = true; }
        }
      }
      for (const [oid, replacement] of replace) {
        if (replacement && !placed.has(oid)) { next.push(replacement); changed = true; }
      }
      if (!changed) continue;
      rows.length = 0;
      for (const row of next) rows.push(row);
      touched.push(table);
    }
    if (touched.length) {
      this.applying = true;
      try { this.port.persist(); }
      finally { this.applying = false; }
    }
    return touched;
  }

  private changeMessages(keys: string[]): SyncMessage[] {
    if (!keys.length) return [];
    const database = this.port.database();
    const lookups = new Map<string, Map<string, MockRecord>>();
    const items: SyncChange[] = [];
    let adjusted = false;
    for (const key of keys) {
      const entry = this.state.records[key];
      const parts = splitKey(key);
      if (!entry || !parts || !mayOriginate(parts.table, this.portal)) continue;
      if (entry[1]) { items.push({ key, c: entry[0], d: 1 }); continue; }
      let lookup = lookups.get(parts.table);
      if (!lookup) {
        lookup = new Map();
        const rows = Object.hasOwn(database, parts.table) ? database[parts.table] : undefined;
        if (Array.isArray(rows)) for (const row of rows) if (!lookup.has(row.oid)) lookup.set(row.oid, row);
        lookups.set(parts.table, lookup);
      }
      const record = lookup.get(parts.oid);
      if (record) items.push({ key, c: entry[0], r: record });
      else { entry[1] = 1; adjusted = true; items.push({ key, c: entry[0], d: 1 }); }
    }
    if (adjusted) this.persistState();
    return this.toMessages(items);
  }

  /** Splits changes into messages small enough to post in one piece. */
  toMessages(items: SyncChange[]): SyncMessage[] {
    const messages: SyncMessage[] = [];
    for (let start = 0; start < items.length; start += BATCH_SIZE) {
      messages.push({ type: 'changes', epoch: this.state.epoch, items: items.slice(start, start + BATCH_SIZE) });
    }
    return messages;
  }

  private digest(): Record<string, string> {
    return Object.fromEntries(Object.entries(this.state.records).map(([key, entry]) => [key, entry[0]]));
  }

  private tick(): string {
    const [lastWall, lastCounter] = parseClock(this.state.last);
    let wall = Math.max(this.now(), lastWall);
    let counter = wall === lastWall ? lastCounter + 1 : 0;
    if (counter > 36 ** 3 - 1) { wall += 1; counter = 0; }
    const clock = `${wall.toString(36).padStart(9, '0')}.${counter.toString(36).padStart(3, '0')}.${this.instance}`;
    this.state.last = clock;
    return clock;
  }

  private read(): SyncState | undefined {
    try {
      const raw = this.store.getItem(SYNC_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) as SyncState : undefined;
      return parsed?.v === 1 && typeof parsed.epoch === 'string' && typeof parsed.last === 'string' && parsed.records && typeof parsed.records === 'object'
        ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  /** Merges with what other tabs of this portal persisted, so no tab overwrites another's pending changes. */
  private persistState(): void {
    const stored = this.read();
    if (stored) this.state = mergeStates(this.state, stored);
    try { this.store.setItem(SYNC_STATE_KEY, JSON.stringify(this.state)); }
    catch (error) { console.error('Mock data synchronization state could not be saved.', error); }
  }

  private emit(event: ReplicaEvent): void {
    for (const listener of [...this.listeners]) listener(event);
  }
}

function parseRecords(json: string): MockRecord[] {
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((record): record is MockRecord => isPlainRecord(record) && typeof record.oid === 'string' && record.oid !== '') : [];
  } catch {
    return [];
  }
}
